import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { keyframes, css } from 'styled-components'
import { Box } from '@chatterbug/aaron'
import { usePageParams } from 'src/utils/params'
import { t } from 'src/lib/i18n-react'

import routes from 'src/lib/rails-routes/rails-routes'
import HeroCard from 'src/ui/HeroCard/HeroCard'
import ActivityLabel from 'src/ui/ActivityLabel/ActivityLabel'
import ButtonGroup from 'src/ui/ButtonGroup/ButtonGroup'
import HeroCardPlaceholder from 'src/ui/HeroCard/HeroCardPlaceholder'
import { getRandomDefaultImage } from 'src/ui/NextActivities/subcomponents/ActivityCard/ActivityCard'

import NextActivitySkeleton from './NextActivitySkeleton'

const KEYFRAMES = keyframes`
  0% { transform: scale(0.9) }
  100% { transform: scale(1) }
`

const LOADING_STATES = {
  initial: 'initial',
  loading: 'loading',
  loaded: 'loaded',
}

const getActivityImageUrl = (activity) =>
  activity.coverImageUrl || getRandomDefaultImage(activity.format)

const NextActivitySelfStudy = ({ activity, onSkipActivity }) => {
  const { l2Code } = usePageParams()
  const prevActivity = useRef(activity)
  const [loadingState, setLoadingState] = useState(LOADING_STATES.initial)
  // to cache a randomly picked image
  const [activityImgUrl, setActivityImgUrl] = useState(
    getActivityImageUrl(activity)
  )
  const isQueueable = activity?.unit?.topic?.isQueueable

  const handleSkipActivity = () => {
    setLoadingState(LOADING_STATES.loading)
    onSkipActivity()
  }

  useEffect(() => {
    if (activity !== prevActivity.current && LOADING_STATES.loading) {
      setLoadingState(LOADING_STATES.loaded)
      prevActivity.current = activity
      setActivityImgUrl(getActivityImageUrl(activity))
    }
  }, [activity, loadingState, setLoadingState, setActivityImgUrl])

  return (
    <NextActivitySkeleton
      headerSection={t('Start here')}
      contentSection={
        <Box
          css={`
            animation: ${loadingState === LOADING_STATES.loaded
              ? css`
                  ${KEYFRAMES} 0.5s ease
                `
              : 'none'};
          `}
        >
          {loadingState === LOADING_STATES.loading ? (
            <HeroCardPlaceholder hasReference />
          ) : (
            <HeroCard
              label={<ActivityLabel type={activity.format} />}
              title={activity.heading}
              imgUrl={activityImgUrl}
              aspectRatio="16:9"
              reference={
                activity.unit && {
                  title: isQueueable
                    ? activity.unit.name
                    : t('Unit %{unitNum} • %{unitName}', {
                        unitNum: activity.unit.label,
                        unitName: activity.unit.name,
                      }),
                  subtitle: activity.unit.topic?.name,
                  linkUrl: routes.advanced_unit(l2Code, activity.unit.id),
                }
              }
              footer={
                <ButtonGroup
                  primaryAction={{
                    label: t('Start studying'),
                    linkUrl: routes.language_study({
                      code: l2Code,
                      activity: activity.identifier,
                      resume: true,
                    }),
                  }}
                  secondaryAction={{
                    label: t('Skip activity'),
                    onClick: handleSkipActivity,
                  }}
                  coachMarkProps={{
                    name: 'coach_mark:start_self_study',
                    title: t('Activities to Practice for Live Lessons'),
                    description: t(
                      'Find recommended activities in Home to warm up before your Live Lessons.'
                    ),
                    align: 'above',
                  }}
                />
              }
            />
          )}
        </Box>
      }
    />
  )
}

NextActivitySelfStudy.propTypes = {
  activity: PropTypes.object.isRequired,
  onSkipActivity: PropTypes.func.isRequired,
}

export default NextActivitySelfStudy
